import { Injectable } from '@angular/core';
import { Observable, combineLatest, EMPTY } from 'rxjs';
import { map, shareReplay, tap, switchMap } from 'rxjs/operators';
import { concatLatestFrom } from '@ngrx/operators';
import { RoiStore, MicroLayoutStore, AssetStore } from '../../stores';
import { ViewerMode, DrawingMode } from '../../types/viewer.types';
import { RoiLabelingEffects } from '../roi-labeling';
import { ProtocolStore } from '@telespot/protocols/ui';
import { Category } from '@telespot/protocols/data-access';
import { LabelingService } from '@analysis/domain';
import { StepTask } from '@telespot/domain';

@Injectable()
export class MicroLabelSelectionEffects {
  private readonly selectedRois$ = combineLatest([
    this.microLayout.selectedRois$,
    this.roiStore.rois$,
    this.assetStore.selectedAsset$,
  ]).pipe(
    map(([selectedRois, rois, selectedAsset]) => {
      const filtered = new Set<string>();
      const assetId = selectedAsset?.id;
      selectedRois.forEach((roiId) => {
        const roi = rois?.[roiId];
        if (roi && roi.labels.size > 0 && (!assetId || roi.assetId === assetId)) {
          filtered.add(roiId);
        }
      });
      return filtered;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private readonly labelsInAllSelectedRois$ = combineLatest([this.selectedRois$, this.roiStore.rois$]).pipe(
    map(([selectedRois, rois]) => {
      if (selectedRois.size === 0) return new Set<string>();
      const roiArray = Array.from(selectedRois)
        .map((roiId) => rois?.[roiId])
        .filter((roi): roi is NonNullable<typeof roi> => roi !== undefined && roi.labels.size > 0);
      if (roiArray.length === 0) return new Set<string>();
      let commonLabels = new Set(roiArray[0].labels.keys());
      for (let i = 1; i < roiArray.length; i++) {
        const currentLabels = new Set(roiArray[i].labels.keys());
        commonLabels = new Set([...commonLabels].filter((label) => currentLabels.has(label)));
      }
      return commonLabels;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  constructor(
    private readonly microLayout: MicroLayoutStore,
    private readonly roiStore: RoiStore,
    private readonly assetStore: AssetStore,
    private readonly roiLabelingEffects: RoiLabelingEffects,
    private readonly protocolStore: ProtocolStore
  ) {}

  // --- Effect ---

  public readonly selectLabel = this.roiStore.effect((labelId$: Observable<string>) =>
    labelId$.pipe(
      concatLatestFrom(() => [
        this.selectedRois$,
        this.microLayout.viewerMode$,
        this.protocolStore.categories$,
        this.microLayout.multiSelection$,
        this.labelsInAllSelectedRois$,
        this.microLayout.selectedLabels$,
      ]),
      tap(
        ([
          labelId,
          selectedRois,
          viewerMode,
          categories,
          multiselectEnabled,
          labelsInAllSelectedRois,
          selectedLabelIds,
        ]) => {
          this.handleSelectLabel(
            labelId,
            selectedRois,
            viewerMode,
            categories,
            multiselectEnabled,
            labelsInAllSelectedRois,
            selectedLabelIds
          );
        }
      ),
      switchMap(() => EMPTY)
    )
  );

  // --- Private ---

  private handleSelectLabel(
    labelId: string,
    selectedRois: Set<string>,
    viewerMode: ViewerMode,
    categories: Record<string, Category> | undefined,
    multiselectEnabled: boolean,
    labelsInAllSelectedRois: Set<string>,
    selectedLabelIds: Set<string> | undefined
  ): void {
    const activeTask = Object.values(categories || {}).find((c: Category) => c.options?.includes(labelId)) as
      | Category
      | undefined;

    if (selectedRois.size > 0) {
      const labelInAllSelectedRois = labelsInAllSelectedRois.has(labelId);
      if (labelInAllSelectedRois) {
        this.roiLabelingEffects.removeLabels({ roiIds: Array.from(selectedRois), labels: [labelId] });
        this.microLayout.selectLabel({ uuid: labelId, replace: !multiselectEnabled });
        return;
      } else {
        if (activeTask) {
          const conflictingLabels = LabelingService.getConflictingLabels(labelId, categories || {});
          if (conflictingLabels.length > 0) {
            this.microLayout.removeLabels(conflictingLabels);
          }
        }
        this.roiLabelingEffects.addLabels({ roiIds: Array.from(selectedRois), labels: [labelId] });
        return;
      }
    }

    if (multiselectEnabled && activeTask && selectedLabelIds?.size) {
      const otherInCategory = activeTask.options.filter((optId) => optId !== labelId && selectedLabelIds.has(optId));
      if (otherInCategory.length > 0) {
        this.microLayout.removeLabels(otherInCategory);
      }
    }
    this.microLayout.selectLabel({ uuid: labelId, replace: !multiselectEnabled });

    if (!activeTask) return;

    const taskType = activeTask.stepTask;
    const isTaggingTask = taskType === StepTask.POSITION || taskType === StepTask.ROIDETECTION;

    if (isTaggingTask && viewerMode !== ViewerMode.drawing) {
      this.microLayout.setViewerMode(ViewerMode.drawing);
      if (taskType === StepTask.POSITION) {
        this.microLayout.setDrawingMode(DrawingMode.center);
      } else if (taskType === StepTask.ROIDETECTION) {
        this.microLayout.setDrawingMode(DrawingMode.boundingBox);
      }
    }
  }
}
